import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@qwickapps/auth-client';
import { config } from '../config/runtime';

export interface JournalEntry {
  id: number;
  goalId: string | null;
  itemId: string | null;
  agent: string;
  event: string;
  payload: Record<string, unknown>;
  ts: string;
}

export interface Item {
  id: string;
  goalId: string;
  kind: 'issue' | 'pr' | 'task' | 'blocker' | 'ci_failure';
  title: string;
  githubRef: string | null;
  owner: string | null;
  status: 'open' | 'in_progress' | 'blocked' | 'done' | 'cancelled';
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Goal {
  id: string;
  title: string;
  description: string | null;
  status: 'active' | 'blocked' | 'done' | 'cancelled';
  owner: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface UseProjectsApiReturn<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

async function fetchWithAuth(
  url: string,
  accessToken: string,
  options?: RequestInit
): Promise<Response> {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...options?.headers,
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }

  return response;
}

export function useProjectsApi<T>(
  endpoint: string,
  transform?: (data: unknown) => T
): UseProjectsApiReturn<T> {
  const { session } = useAuth();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    if (!session?.accessToken) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = config.apiUrl || 'https://api.qwickforge.com';
      const url = `${baseUrl}${endpoint}`;
      const response = await fetchWithAuth(url, session.accessToken);
      const json = await response.json();
      const result = transform ? transform(json) : (json as T);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [endpoint, session?.accessToken, transform]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export function useJournal(limit = 500, agent?: string, goalId?: string) {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  if (agent) params.set('agent', agent);
  if (goalId) params.set('goal_id', goalId);

  return useProjectsApi<{ entries: JournalEntry[]; limit: number }>(
    `/api/journal?${params.toString()}`,
    (data) => {
      const d = data as { entries: JournalEntry[]; limit: number };
      return {
        entries: d.entries.map((e) => ({
          ...e,
          ts: new Date(e.ts).toISOString(),
          createdAt: new Date(e.ts).toISOString(),
        })),
        limit: d.limit,
      };
    }
  );
}

export function useGoals(status?: string) {
  const params = new URLSearchParams();
  params.set('limit', '100');
  if (status && status !== 'all') params.set('status', status);

  return useProjectsApi<{ goals: Goal[]; limit: number; offset: number }>(
    `/api/goals?${params.toString()}`,
    (data) => data as { goals: Goal[]; limit: number; offset: number }
  );
}

export function useGoalItems(goalId: string | null) {
  return useProjectsApi<{ items: Item[]; limit: number; offset: number }>(
    goalId ? `/api/goals/${goalId}/items?limit=200` : '/api/goals',
    (data) => data as { items: Item[]; limit: number; offset: number }
  );
}
